import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Lock } from "lucide-react";
import { IMG } from "../../lib/assets";

export const Hero = ({ onCta }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const cardY = useTransform(scrollYProgress, [0, 1], ["0%", "-10%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-8%"]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#FF4D0A] text-[#0B0B0C]"
      data-testid="hero-section"
    >
      {/* subtle grain */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.06] mix-blend-overlay bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22180%22 height=%22180%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%222%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/></svg>')]" />

      {/* Content */}
      <motion.div
        style={{ y: textY }}
        className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 w-full min-h-[calc(100svh-56px)] md:min-h-[100svh] flex flex-col justify-end pb-8 sm:pb-12 pt-8 md:pt-28"
      >
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-8 items-center">
          {/* Left: message + CTA on one grid */}
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="eyebrow !text-[#0B0B0C]/80 mb-4 sm:mb-8 flex items-center gap-3"
              data-testid="hero-eyebrow"
            >
              <span className="live-dot inline-block h-1.5 w-1.5 rounded-full bg-[#0B0B0C]" />
              Know your day before it starts
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="h-heavy leading-[0.9] tracking-[-0.03em] text-[clamp(36px,9.5vw,44px)] sm:text-[64px] md:text-[72px] lg:text-[84px] whitespace-nowrap"
              data-testid="hero-headline"
            >
              Your body runs
              <br />
              on a clock.
              <br />
              <span className="text-[#F2F0EB]">Use it.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 sm:mt-9 max-w-xl text-base sm:text-lg md:text-xl text-[#0B0B0C]/80 font-medium leading-relaxed"
              data-testid="hero-subheadline"
            >
              Know your peak. Know your dip.
              <br />
              Choose your window.
            </motion.p>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, delay: 0.9 }}
              className="mt-4 max-w-xl text-sm sm:text-base text-[#0B0B0C]/70 font-medium leading-relaxed"
              data-testid="hero-seen-line"
            >
              For the ones who feel everything and still have to decide.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.95, ease: [0.22, 1, 0.36, 1] }}
              className="mt-7 sm:mt-10 hidden sm:flex flex-col items-start gap-3"
            >
              <button
                onClick={onCta}
                className="cta-dark w-auto px-10 py-4 text-base"
                data-testid="hero-cta-button"
              >
                Create my Source Code
              </button>
              <p
                className="flex items-center gap-1.5 text-xs text-[#0B0B0C]/70 font-medium"
                data-testid="hero-privacy-badge"
              >
                <Lock size={12} className="text-[#0B0B0C] shrink-0" />
                Computed on your device. Your birth data never leaves the app.
              </p>
            </motion.div>
          </div>

          {/* Right: device in hand */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            style={{ y: cardY }}
            className="relative lg:col-span-5 flex flex-col items-center lg:items-end gap-4 lg:h-[560px]"
            data-testid="hero-floating-card"
          >
            <img
              src={IMG.heroPhone}
              alt="A hand holding a phone with the SOURCECODED NOW screen: a score of 80, a best window of 10:00 AM to 2:00 PM and today's energy read"
              className="relative w-full max-w-[380px] sm:max-w-[460px] md:max-w-[520px] lg:absolute lg:top-0 lg:right-0 lg:max-w-none lg:w-[600px] lg:translate-x-10"
              style={{
                WebkitMaskImage: "linear-gradient(180deg, #000 86%, transparent 100%)",
                maskImage: "linear-gradient(180deg, #000 86%, transparent 100%)",
              }}
              data-testid="hero-phone-image"
            />
            {/* Mobile-only CTA under the device */}
            <div className="sm:hidden -mt-2 flex flex-col items-center gap-3">
              <button
                onClick={onCta}
                className="cta-dark w-auto px-6 py-3.5 text-sm"
                data-testid="hero-cta-button-mobile"
              >
                Create my Source Code
              </button>
              <p
                className="flex items-center gap-1.5 text-[11px] text-[#0B0B0C]/70 font-medium"
                data-testid="hero-privacy-badge-mobile"
              >
                <Lock size={11} className="text-[#0B0B0C] shrink-0" />
                Computed on your device. Never leaves the app.
              </p>
            </div>
          </motion.div>
        </div>

        {/* bottom meta row */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="relative mt-6 sm:mt-10 pt-4 sm:pt-6 border-t border-[#0B0B0C]/20 flex items-center justify-between gap-4 text-[10px] sm:text-[11px] tracking-[0.15em] sm:tracking-[0.2em] uppercase text-[#0B0B0C]/70 font-mono-brand"
          data-testid="hero-meta-row"
        >
          <span>iOS &nbsp;•&nbsp; Available now</span>
          <span className="hidden sm:inline">Ver 01 / iOS</span>
        </motion.div>
      </motion.div>
    </section>
  );
};
